import React, { useState, useEffect } from 'react';
import merge from 'lodash.merge';
import { ReactComponent as BarIcon } from '../../assets/icons/metrics-icon.svg';
import { ReactComponent as MoreIcon } from '../../assets/icons/more-icon.svg';
import './Tile.css';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import Table from './Table';
import {
  Chart as ChartJS, 
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement
} from 'chart.js';
import {
  Bar,
  Line,
  Pie,
  Doughnut,
  PolarArea,
  Radar,
  Bubble,
  Scatter,
} from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels,
  ArcElement,
  PointElement,
  LineElement
);

// Formats numeric values to a maximum of 2 decimals (rounded) without adding
// unnecessary trailing zeros. Non-numeric inputs are returned unchanged.
function formatNumberToMaxTwoDecimals(input) {
  const numeric = typeof input === 'string' ? Number(input) : input;
  if (typeof numeric === 'number' && Number.isFinite(numeric)) {
    return numeric.toLocaleString(undefined, {
      maximumFractionDigits: 2,
      useGrouping: false
    });
  }
  return input;
}


// Map chart types to Chart.js components by their exact names
const chartComponents = {
  Bar,
  Line,
  Pie,
  Doughnut,
  PolarArea,
  Radar,
  Bubble,
  Scatter,
};

const COLOR_PALETTE = [
  { bg: 'rgba(54, 162, 235, 0.5)', border: 'rgba(54, 162, 235, 1)' },   // Blue
  { bg: 'rgba(255, 99, 132, 0.5)', border: 'rgba(255, 99, 132, 1)' },   // Red
  { bg: 'rgba(255, 206, 86, 0.5)', border: 'rgba(255, 206, 86, 1)' },   // Yellow
  { bg: 'rgba(75, 192, 192, 0.5)', border: 'rgba(75, 192, 192, 1)' },   // Teal
  { bg: 'rgba(153, 102, 255, 0.5)', border: 'rgba(153, 102, 255, 1)' }, // Purple
];

const defaultChartJson = {
  options: {
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 300,
      easing: 'easeOutQuart',
    },
    layout: {
      padding: 0
    },
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          color: '#333',
          font: {
            size: 12,
            family: 'var(--font-regular)',
          },
          boxWidth: 12,
          usePointStyle: true,
          pointStyle: 'rectRounded'
        },
      },
      title: {
        display: false,
      },
      datalabels: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(0,0,0,0.7)',
        titleFont: { size: 13 },
        bodyFont: { size: 12 },
        padding: 2,
      }
    },
    scales: {
      x: {
        stacked: false,
        title: {
          display: false,
          color: '#333',
          font: {
            size: 12,
            weight: 'bold',
          },
        },
        ticks: {
          padding: 4,
          color: '#4F4F4F',
          font: {
            size: 11,
            family: 'var(--font-regular)',
          },
          callback: function(value, index, values) {
            const rawLabel = this.getLabelForValue(value);
            const formatted = formatNumberToMaxTwoDecimals(rawLabel);
            const labelStr = String(formatted);
            const maxLength = 18; // Maximum characters before truncation
            if (labelStr.length > maxLength) {
              return labelStr.substring(0, maxLength) + '...';
            }
            return formatted;
          }
        },
        grid: {
          color: '#e2e2e2',
          lineWidth: 0.8,
          drawBorder: false,
        },
      },
      y: {
        stacked: false,
        title: {
          display: false,
          color: '#333',
          font: {
            size: 12,
            weight: 'bold',
          },
        },
        ticks: {
          padding: 4,
          color: '#4F4F4F',
          font: {
            size: 11,
            family: 'Arial, sans-serif',
          },
          callback: function(value) {
            return formatNumberToMaxTwoDecimals(value);
          }
        },
        grid: {
          color: '#e2e2e2',
          lineWidth: 0.8,
          drawBorder: false,
        },
        beginAtZero: true,
      },
      y1: {
        display: false,
        position: 'right',
        grid: {
          drawOnChartArea: false,
        },
        ticks: {
          padding: 4,
          color: '#4F4F4F',
          font: {
            size: 11,
            family: 'var(--font-regular)',
          },
          callback: function(value) {
            return formatNumberToMaxTwoDecimals(value);
          }
        },
        title: {
          display: false,
          color: '#333',
          font: {
            size: 12,
            weight: 'bold',
          },
        },
      }
    },
    hover: {
      mode: 'nearest',
      intersect: true,
    }
  }
};

const Tile = ({ chartJson, title, subtitle, includeMoreButton, onMoreClick, minH, minW, disableAnimation }) => {
  const [chartData, setChartData] = useState(defaultChartJson);
  const [ChartComponent, setChartComponent] = useState(null);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    // Only process chart data if chartJson changes
    let parsedChartJson = {};
    try {
      parsedChartJson = chartJson ? JSON.parse(chartJson) : {};
    } catch (error) {
      parsedChartJson = chartJson || {};
    }
    const mergedChartData = merge({}, defaultChartJson, parsedChartJson);
    
    // Disable animations if requested
    if (disableAnimation && mergedChartData.options) {
      mergedChartData.options.animation = {
        duration: 0
      };
      mergedChartData.options.responsive = true;
      mergedChartData.options.maintainAspectRatio = false;
    }
    
    const finalChartData = applyDatasetColors(mergedChartData);
    setChartData(finalChartData);

    // Set the ChartComponent based on the chart type
    try {
      const chartTypeKey = Object.keys(chartComponents).find(
        (chartName) => chartName.toLowerCase() === mergedChartData.type.toLowerCase()
      );
      const ChartComponent = chartComponents[chartTypeKey] || null;
      setChartComponent(ChartComponent);
    } catch (error) {
      // Handle error silently
    }
    
    setInitialized(true);
  }, [chartJson, disableAnimation]);
  

  const handleMoreClick = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    onMoreClick();
  }

  function applyDatasetColors(config) {
    if (!config || !config.data || !config.data.datasets) return config;
  
    // Check if any dataset uses the secondary y-axis
    const hasSecondaryAxis = config.data.datasets.some(ds => ds.yAxisID === 'y1');
    
    // Only show the secondary axis if at least one dataset uses it
    if (config.options && config.options.scales && config.options.scales.y1) {
      config.options.scales.y1.display = hasSecondaryAxis;
    }
  
    // Set legend position to 'right' for pie/doughnut/polararea
    const pieLikeTypes = ['pie', 'doughnut', 'polararea'];
    if (
      config.options &&
      config.type &&
      pieLikeTypes.includes(config.type.toLowerCase())
    ) {
      config.options.plugins = config.options.plugins || {};
      config.options.plugins.legend = config.options.plugins.legend || {};
      config.options.plugins.legend.position = 'right';
    }
  
    config.data.datasets = config.data.datasets.map((ds, i) => {
      const color = COLOR_PALETTE[i % COLOR_PALETTE.length];

      // If Pie/Doughnut/PolarArea, assign color per data point
      const isPieLike = ['pie', 'doughnut', 'polararea'].includes(
        (ds.type || config.type || '').toLowerCase()
      );
      let backgroundColor = ds.backgroundColor;
      let borderColor = ds.borderColor;

      if (isPieLike && ds.data && Array.isArray(ds.data)) {
        backgroundColor = ds.backgroundColor || ds.data.map((_, idx) => COLOR_PALETTE[idx % COLOR_PALETTE.length].bg);
        borderColor = ds.borderColor || ds.data.map((_, idx) => COLOR_PALETTE[idx % COLOR_PALETTE.length].border);
      } else {
        backgroundColor = ds.backgroundColor || color.bg;
        borderColor = ds.borderColor || color.border;
      }
  
      return {
        ...ds,
        backgroundColor,
        borderColor,
        borderWidth: ds.borderWidth ?? 2,
        pointRadius: ds.type === 'line' || (config.type === 'line' && !ds.type) ? 0 : 
                     ds.type === 'scatter' ? 4 : undefined,
        fill: ds.type === 'line' && ds.fill !== false ? true : false,
        borderRadius: ds.type === 'bar' || (config.type === 'bar' && !ds.type) ? 2 : 0,
      };
    });
  
    return config;
  }  

  // Memoize the chart content to prevent re-rendering
  const chartContent = React.useMemo(() => {
    if (!initialized) return null;
    
    if (chartData.type === "table") {
      return <Table className="table" data={chartData.data}/>;
    } else if (chartData.data && ChartComponent) {
      return <ChartComponent className="chart" data={chartData.data} options={chartData.options} />;
    } else {
      return (
        <div className="no-chart-data">
          <BarIcon />
          <p>No chart data yet.</p>
        </div>
      );
    }
  }, [initialized, chartData, ChartComponent]);

  return (
    <div 
      className="tile-content" 
      style={{ 
        minHeight: minH ? `${minH}px` : undefined, 
        minWidth: minW ? `${minW}` : undefined 
      }} 
      onClick={(e) => e.stopPropagation()}
    >
      {title || subtitle || includeMoreButton && <div className="tile-header">
        <div className="tile-header-left">
            {title && <h3 className="tile-title">{title}</h3>}
            {subtitle && <p className="tile-subtitle">{subtitle}</p>}
        </div>
        {includeMoreButton && (
          <div className="tile-header-right" onClick={handleMoreClick}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <MoreIcon className="more-icon"/>
          </div>
        )}
      </div>}
      <div className="chart-container">
        {chartContent}
      </div>
    </div>
  );
};

export default Tile;
